import { LogLevel, type Configuration, type PopupRequest } from '@azure/msal-browser';

export type AuthMode = 'msal' | 'stub';

const env = import.meta.env;

/**
 * `stub` bypasses Entra entirely and runs against a fixed local identity, which
 * lets the app be developed and tested without provisioning a CIAM tenant.
 */
export const authMode: AuthMode = env.VITE_AUTH_MODE === 'stub' ? 'stub' : 'msal';

const tenantSubdomain = env.VITE_CIAM_TENANT_SUBDOMAIN ?? '';
const tenantId = env.VITE_CIAM_TENANT_ID ?? '';
const clientId = env.VITE_CIAM_CLIENT_ID ?? '';

/** Entra External ID (CIAM) authority. Note the `ciamlogin.com` host, not `login.microsoftonline.com`. */
export const authority = `https://${tenantSubdomain}.ciamlogin.com/${tenantId}/v2.0`;

export const apiScope = env.VITE_API_SCOPE ?? '';

export const isAuthConfigured =
  authMode === 'stub' || (tenantSubdomain !== '' && tenantId !== '' && clientId !== '');

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    knownAuthorities: [`${tenantSubdomain}.ciamlogin.com`],
    redirectUri: `${globalThis.location?.origin ?? ''}/`,
    postLogoutRedirectUri: `${globalThis.location?.origin ?? ''}/`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage keeps tokens out of other tabs; iOS purges localStorage aggressively anyway.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

export const loginRequest: PopupRequest = {
  scopes: apiScope ? [apiScope, 'openid', 'profile', 'offline_access'] : ['openid', 'profile'],
};

export const tokenRequest = {
  scopes: apiScope ? [apiScope] : ['openid'],
};
